import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { EventSpace } from "@models/space";
import { DateTime } from "luxon";
import { EventTicketBatch } from "../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";

type BuildSpaceEditModalFormParams = {
  formBuilder: FormBuilder;
  space?: EventSpace;
  ticketBatches?: EventTicketBatch[];
};

export type SpaceEditModalForm = FormGroup<{
  title: FormControl<string>;
  description: FormControl<string>;
  quantity: FormControl<number | null>;
  price: FormControl<number | null>;
  consumption: FormControl<number | null>;
  saleStart: FormControl<Date | null>;
  saleEnd: FormControl<Date | null>;
}>;

export function buildSpaceEditModalForm(
  params: BuildSpaceEditModalFormParams,
): SpaceEditModalForm {
  const { formBuilder, space, ticketBatches } = params;

  const defaultDates = getDefaultDatesFromBatches(ticketBatches ?? []);

  return formBuilder.group(
    {
      title: formBuilder.control<string>(space?.title ?? "", {
        validators: [Validators.required],
      }),
      description: formBuilder.control<string>(space?.description ?? "", {
        validators: [Validators.required],
      }),
      quantity: formBuilder.control<number | null>(space?.quantity ?? null, {
        validators: [Validators.required, Validators.min(1)],
      }),
      price: formBuilder.control<number | null>(space?.price ?? null, {
        validators: [Validators.required, Validators.min(1)],
      }),
      consumption: formBuilder.control<number | null>(
        space?.consumption ?? null,
        { validators: [Validators.required, Validators.min(0)] },
      ),
      saleStart: formBuilder.control<Date | null>(
        space?.sale_start ? new Date(space.sale_start) : defaultDates.start,
        { validators: [Validators.required] },
      ),
      saleEnd: formBuilder.control<Date | null>(
        space?.sale_end ? new Date(space.sale_end) : defaultDates.end,
        { validators: [Validators.required] },
      ),
    },
    {
      validators: [saleEndAfterStartValidator()],
    },
  ) as SpaceEditModalForm;
}

function getDefaultDatesFromBatches(ticketBatches: EventTicketBatch[]): {
  start: Date;
  end: Date | null;
} {
  const now = DateTime.local();
  const fallbackStart = now.plus({ minutes: 10 });

  const validStarts = ticketBatches
    .map((b) => DateTime.fromISO(String(b.sale_start)))
    .filter((d) => d.isValid);

  const earliestStart =
    validStarts.length > 0
      ? validStarts.reduce((min, d) => (d < min ? d : min))
      : null;

  // Usa o início de venda do primeiro lote; se estiver no passado (ou ausente),
  // usa agora + 10 minutos.
  const start =
    earliestStart && earliestStart > now ? earliestStart : fallbackStart;

  const validEnds = ticketBatches
    .map((b) => DateTime.fromISO(String(b.sale_end)))
    .filter((d) => d.isValid);

  const end =
    validEnds.length > 0
      ? validEnds.reduce((max, d) => (d > max ? d : max))
      : null;

  return { start: start.toJSDate(), end: end ? end.toJSDate() : null };
}

function saleEndAfterStartValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const start = group.get("saleStart")?.value as Date | null;
    const end = group.get("saleEnd")?.value as Date | null;

    if (!start || !end) return null;

    if (end.getTime() <= start.getTime()) {
      return { saleEndBeforeStart: true };
    }

    return null;
  };
}
